/**
 * Shared attachment staging for Benny chat composers (header BennyChat +
 * Simple Mode). Pure client-side helpers only — no React, no toasts: callers
 * own their own state and surface `error` however their surface does.
 *
 * The rules here MIRROR the server's validation on POST /ai/assistant/chat/stream
 * (4 files, images by `image/*`, a fixed document set, 4.5 MB per document,
 * 20 MB total decoded). Keeping them in sync means a rejection is a friendly
 * toast instead of a 400 after the upload has already been paid for.
 */

import type { AssistantChatAttachment } from '@/lib/api/assistant.api'

import {
  formatAttachmentBytes,
  MAX_ATTACHMENTS,
  MAX_DOCUMENT_BYTES,
  MAX_IMAGE_BYTES,
  MAX_TOTAL_ATTACHMENT_BYTES,
  resolveAttachmentContentType,
} from '@scrambl/shared'

/**
 * Pending attachment bound to a local preview URL. Kept on the optimistic
 * message until the request resolves, then dropped — we don't persist
 * client-side previews.
 */
export interface PendingAttachment extends AssistantChatAttachment {
  id: string
  /** object URL for thumbnail rendering — must be revoked when removed. */
  previewUrl: string
  size: number
  kind: 'image' | 'document'
}

// The limits and the accepted document set live in `@scrambl/shared` next to
// the mobile picker's copy, so both clients reject the same files the server
// would. `MAX_ATTACHMENT_BYTES` keeps its web name as the image ceiling.
export {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_DOCUMENT_BYTES,
  MAX_IMAGE_BYTES as MAX_ATTACHMENT_BYTES,
  MAX_TOTAL_ATTACHMENT_BYTES,
} from '@scrambl/shared'

/**
 * Resolve the content type we'll actually send. The extension map wins over
 * the browser's guess — see the shared resolver for why.
 */
function resolveContentType(file: File): string | null {
  return resolveAttachmentContentType(file.name, file.type)
}

/**
 * Read a File into a base64-encoded `AssistantChatAttachment`. Strips the
 * `data:<mime>;base64,` prefix so the backend gets clean bytes.
 */
export async function fileToAttachment(file: File): Promise<PendingAttachment> {
  const dataUrl: string = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () =>
      reject(reader.error || new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
  const base64 = dataUrl.includes(',')
    ? (dataUrl.split(',', 2)[1] ?? '')
    : dataUrl
  const contentType =
    resolveContentType(file) || file.type || 'application/octet-stream'
  return {
    id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    contentType,
    base64,
    fileName: file.name,
    previewUrl: URL.createObjectURL(file),
    size: file.size,
    kind: contentType.startsWith('image/') ? 'image' : 'document',
  }
}

/** 'image' keeps a composer image-only; 'any' also takes documents. */
export type AttachmentMode = 'image' | 'any'

export interface AcceptAttachmentsResult {
  accepted: PendingAttachment[]
  /** First rejection reason, if any — surface as a single toast. */
  error?: string
}

/**
 * Validate + read a batch of dropped/pasted/picked files against `existing`.
 * Everything acceptable is returned; the first rejection reason comes back as
 * `error` so the caller raises exactly one toast per batch.
 */
export async function acceptAttachmentFiles(
  files: File[] | FileList,
  existing: PendingAttachment[],
  mode: AttachmentMode = 'any',
): Promise<AcceptAttachmentsResult> {
  const all = Array.from(files)
  if (all.length === 0) return { accepted: [] }

  let error: string | undefined
  const reject = (reason: string) => {
    if (!error) error = reason
  }

  const incoming = all.filter(file => {
    const contentType = resolveContentType(file)
    if (!contentType) return false
    return mode === 'image' ? contentType.startsWith('image/') : true
  })
  if (incoming.length === 0) {
    return {
      accepted: [],
      error:
        mode === 'image'
          ? 'Only image attachments are supported.'
          : 'Only images and PDF, TXT, CSV, MD, DOCX or XLSX files can be attached.',
    }
  }
  if (incoming.length < all.length) {
    reject(
      mode === 'image'
        ? 'Only image attachments are supported.'
        : 'Only images and PDF, TXT, CSV, MD, DOCX or XLSX files can be attached.',
    )
  }

  const room = MAX_ATTACHMENTS - existing.length
  if (room <= 0) {
    return {
      accepted: [],
      error: `Up to ${MAX_ATTACHMENTS} attachments per message.`,
    }
  }
  if (incoming.length > room) {
    reject(`Up to ${MAX_ATTACHMENTS} attachments per message.`)
  }

  let totalBytes = existing.reduce((sum, att) => sum + att.size, 0)
  const accepted: PendingAttachment[] = []
  for (const file of incoming.slice(0, room)) {
    const isImage = (resolveContentType(file) ?? '').startsWith('image/')
    const limit = isImage ? MAX_IMAGE_BYTES : MAX_DOCUMENT_BYTES
    if (file.size > limit) {
      reject(
        `${file.name} is larger than ${formatAttachmentBytes(limit)}. Skipped.`,
      )
      continue
    }
    if (totalBytes + file.size > MAX_TOTAL_ATTACHMENT_BYTES) {
      reject(
        `Attachments exceed ${formatAttachmentBytes(MAX_TOTAL_ATTACHMENT_BYTES)} in total.`,
      )
      continue
    }
    try {
      accepted.push(await fileToAttachment(file))
      totalBytes += file.size
    } catch {
      reject(`Could not read ${file.name}.`)
    }
  }

  return error ? { accepted, error } : { accepted }
}

/** Strip the client-only fields before putting attachments on the wire. */
export function toRequestAttachments(
  attachments: PendingAttachment[],
): AssistantChatAttachment[] {
  return attachments.map(({ contentType, base64, fileName }) => ({
    contentType,
    base64,
    ...(fileName ? { fileName } : {}),
  }))
}
